<template>
  <div :class="{ 'py-0': embedded, 'py-8': !embedded }">
    <UPageHeader
      v-if="!embedded"
      title="Aufgaben"
      description="Alle Aufgaben, die für Sie oder Ihre Gruppen bereitstehen."
      class="border-b dark:border-gray-800 bg-white dark:bg-gray-900 sticky top-0 z-20"
    >
      <template #links>
        <div class="flex items-center gap-2">
          <UButton
            :loading="loading"
            icon="i-heroicons-arrow-path"
            color="neutral"
            variant="ghost"
            class="sm:hidden rounded-full"
            @click="fetchTasks"
          />
          <UButton
            :loading="loading"
            icon="i-heroicons-arrow-path"
            label="Aktualisieren"
            color="neutral"
            variant="subtle"
            class="hidden sm:flex rounded-xl font-bold"
            @click="fetchTasks"
          />
        </div>
      </template>
    </UPageHeader>

    <UPageBody :ui="{ base: embedded ? 'mt-0 pb-0 space-y-6' : 'mt-8 pb-24 space-y-6' }">
      <div v-if="canManageWorkflows" class="flex items-center justify-start">
        <UTabs
          v-model="activeTab"
          :items="tabs"
          class="w-full sm:w-auto"
        />
      </div>

      <div class="space-y-6">
        <!-- Aufgaben Liste -->
        <UCard
          :ui="{
            header: 'px-4 py-3 border-b border-default',
            body: 'p-0',
          }"
        >
          <template #header>
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div class="flex flex-col sm:flex-row sm:items-center gap-4 flex-1">
                <h3 class="text-lg font-bold">
                  {{ activeTab === 'all-instances' ? 'Alle Workflow-Instanzen' : 'Offene Aufgaben' }}
                </h3>
              </div>
              <div class="flex items-center gap-4 shrink-0">
                <UBadge color="neutral" variant="subtle" size="md" class="font-extrabold px-4 py-1 rounded-full">
                  {{ filteredTasks.length }}
                </UBadge>
              </div>
            </div>
          </template>

              <UTable
                v-model:sorting="sorting"
                :columns="columns"
                :data="filteredTasks"
                :on-select="(_: unknown, row: { original: InstanceState }) => router.push(embedded ? `/embedded/tasks/i/${row.original.guid}` : `/tasks/${row.original.guid}`)"
                :ui="{
                  base: 'table-fixed min-w-full',
                  tr: 'group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors',
                  th: 'text-[11px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 px-4 py-3 border-b border-default',
                  td: 'px-4 py-3'
                }"
              >
            <!-- Header slots for sorting -->
            <template #objectNumber-header="{ column }">
              <div class="flex items-center gap-1">
                <div class="flex items-center gap-1 cursor-pointer select-none" @click="column.toggleSorting()">
                  <span>Objekt</span>
                  <UIcon v-if="column.getIsSorted()" :name="column.getIsSorted() === 'asc' ? 'i-heroicons-bars-arrow-up' : 'i-heroicons-bars-arrow-down'" class="w-4 h-4 text-gray-900 dark:text-white shrink-0" />
                </div>
                <UPopover :ui="{ content: 'w-72' }">
                  <UButton
                    icon="i-heroicons-funnel"
                    size="xs"
                    variant="link"
                    color="neutral"
                    class="p-0 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:ring-0 shadow-none transition-colors"
                    :trailing="false"
                    :class="{ 'text-gray-900 dark:text-white font-bold': searchTerm || selectedTenantFilter }"
                    @click.stop
                  />
                  <template #content>
                    <div class="p-4 space-y-4">
                      <div class="space-y-2">
                        <span class="text-[10px] font-bold uppercase tracking-wider text-gray-400">Suche</span>
                        <UInput
                          v-model="searchTerm"
                          placeholder="Belegnummer..."
                          icon="i-heroicons-magnifying-glass"
                          size="sm"
                          autofocus
                        />
                      </div>
                      <div v-if="availableTenants.length > 1" class="space-y-2">
                        <span class="text-[10px] font-bold uppercase tracking-wider text-gray-400">Mandant</span>
                        <USelectMenu
                          v-model="selectedTenantFilter"
                          :items="tenantOptions"
                          placeholder="Alle Mandanten"
                          size="sm"
                        />
                      </div>
                      <UButton
                        v-if="searchTerm || selectedTenantFilter"
                        label="Filter zurücksetzen"
                        size="xs"
                        variant="ghost"
                        block
                        @click="searchTerm = ''; selectedTenantFilter = undefined"
                      />
                    </div>
                  </template>
                </UPopover>
              </div>
            </template>
            <template #blueprint-header="{ column }">
              <div class="flex items-center gap-1">
                <div class="flex items-center gap-1 cursor-pointer select-none" @click="column.toggleSorting()">
                  <span>Workflow</span>
                  <UIcon v-if="column.getIsSorted()" :name="column.getIsSorted() === 'asc' ? 'i-heroicons-bars-arrow-up' : 'i-heroicons-bars-arrow-down'" class="w-4 h-4 text-gray-900 dark:text-white shrink-0" />
                </div>
                <USelectMenu
                  v-model="selectedBlueprintFilter"
                  :items="blueprintOptions"
                  multiple
                  placeholder="Workflow filtern..."
                  :ui="{ content: 'w-72' }"
                  :trailing="false"
                >
                  <UButton
                    icon="i-heroicons-funnel"
                    size="xs"
                    variant="link"
                    color="neutral"
                    class="p-0 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:ring-0 shadow-none transition-colors"
                    :trailing="false"
                    :class="{ 'text-gray-900 dark:text-white font-bold': selectedBlueprintFilter && selectedBlueprintFilter.length > 0 }"
                    @click.stop
                  />
                </USelectMenu>
              </div>
            </template>
            <template #group-header="{ column }">
              <div class="flex items-center gap-1">
                <div class="flex items-center gap-1 cursor-pointer select-none" @click="column.toggleSorting()">
                  <span class="hidden sm:inline">Gruppe</span>
                  <UIcon v-if="column.getIsSorted()" :name="column.getIsSorted() === 'asc' ? 'i-heroicons-bars-arrow-up' : 'i-heroicons-bars-arrow-down'" class="w-4 h-4 text-gray-900 dark:text-white shrink-0" />
                </div>
                <USelectMenu
                  v-model="selectedGroupFilter"
                  :items="groupOptions"
                  multiple
                  placeholder="Gruppe filtern..."
                  :ui="{ content: 'w-72' }"
                  :trailing="false"
                >
                  <UButton
                    icon="i-heroicons-funnel"
                    size="xs"
                    variant="link"
                    color="neutral"
                    class="p-0 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:ring-0 shadow-none transition-colors"
                    :trailing="false"
                    :class="{ 'text-gray-900 dark:text-white font-bold': selectedGroupFilter && selectedGroupFilter.length > 0 }"
                    @click.stop
                  />
                </USelectMenu>
              </div>
            </template>
            <template #currentStep-header="{ column }">
              <div class="flex items-center gap-1">
                <div class="flex items-center gap-1 cursor-pointer select-none" @click="column.toggleSorting()">
                  <span>Schritt</span>
                  <UIcon v-if="column.getIsSorted()" :name="column.getIsSorted() === 'asc' ? 'i-heroicons-bars-arrow-up' : 'i-heroicons-bars-arrow-down'" class="w-4 h-4 text-gray-900 dark:text-white shrink-0" />
                </div>
                <USelectMenu
                  v-model="selectedStepFilter"
                  :items="stepOptions"
                  multiple
                  placeholder="Schritt filtern..."
                  :ui="{ content: 'w-72' }"
                  :trailing="false"
                >
                  <UButton
                    icon="i-heroicons-funnel"
                    size="xs"
                    variant="link"
                    color="neutral"
                    class="p-0 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:ring-0 shadow-none transition-colors"
                    :trailing="false"
                    :class="{ 'text-gray-900 dark:text-white font-bold': selectedStepFilter && selectedStepFilter.length > 0 }"
                    @click.stop
                  />
                </USelectMenu>
              </div>
            </template>
            <template #workflowState-header="{ column }">
              <div class="flex items-center gap-1">
                <div class="flex items-center gap-1 cursor-pointer select-none" @click="column.toggleSorting()">
                  <span>Status</span>
                  <UIcon v-if="column.getIsSorted()" :name="column.getIsSorted() === 'asc' ? 'i-heroicons-bars-arrow-up' : 'i-heroicons-bars-arrow-down'" class="w-4 h-4 text-gray-900 dark:text-white shrink-0" />
                </div>
                <USelectMenu
                  v-model="selectedStateFilter"
                  :items="stateOptions"
                  multiple
                  placeholder="Status filtern..."
                  :ui="{ content: 'w-72' }"
                  :trailing="false"
                >
                  <UButton
                    icon="i-heroicons-funnel"
                    size="xs"
                    variant="link"
                    color="neutral"
                    class="p-0 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:ring-0 shadow-none transition-colors"
                    :trailing="false"
                    :class="{ 'text-gray-900 dark:text-white font-bold': selectedStateFilter?.length }"
                    @click.stop
                  />
                </USelectMenu>
              </div>
            </template>
            <template #claimedBy-header="{ column }">
              <div class="flex items-center gap-1">
                <div class="flex items-center gap-1 cursor-pointer select-none" @click="column.toggleSorting()">
                  <span>Besitzer</span>
                  <UIcon v-if="column.getIsSorted()" :name="column.getIsSorted() === 'asc' ? 'i-heroicons-bars-arrow-up' : 'i-heroicons-bars-arrow-down'" class="w-4 h-4 text-gray-900 dark:text-white shrink-0" />
                </div>
                <USelectMenu
                  v-model="selectedUserFilter"
                  :items="userOptions"
                  multiple
                  placeholder="Besitzer filtern..."
                  :ui="{ content: 'w-64' }"
                  :trailing="false"
                >
                  <UButton
                    icon="i-heroicons-funnel"
                    size="xs"
                    variant="link"
                    color="neutral"
                    class="p-0 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:ring-0 shadow-none transition-colors"
                    :trailing="false"
                    :class="{ 'text-gray-900 dark:text-white font-bold': selectedUserFilter && selectedUserFilter.length > 0 }"
                    @click.stop
                  />
                </USelectMenu>
              </div>
            </template>
            <template #startTime-header="{ column }">
              <div class="flex items-center gap-1 cursor-pointer select-none" @click="column.toggleSorting()">
                <span>Gestartet</span>
                <UIcon v-if="column.getIsSorted()" :name="column.getIsSorted() === 'asc' ? 'i-heroicons-bars-arrow-up' : 'i-heroicons-bars-arrow-down'" class="w-4 h-4 text-gray-900 dark:text-white shrink-0" />
              </div>
            </template>

            <template #objectNumber-cell="{ row }">
              <div class="flex items-center gap-2">
                <span class="font-bold text-gray-900 dark:text-white">
                  {{ row.original.objectNumber }}
                </span>
              </div>
            </template>

            <template #blueprint-cell="{ row }">
              <div class="flex items-center gap-2 group/link cursor-help" @click.stop="viewBlueprint(row.original)">
                <UIcon name="i-heroicons-share" class="w-4 h-4 text-gray-400 group-hover/link:text-gray-600 dark:group-hover/link:text-gray-300 transition-colors" />
                <span class="text-sm font-medium group-hover/link:text-gray-900 dark:group-hover/link:text-white transition-colors">
                  {{ row.original.blueprint?.id || row.original.blueprintId }}
                </span>
                <UBadge v-if="row.original.blueprint?.version" color="neutral" variant="subtle" size="xs" class="opacity-70 group-hover/link:opacity-100">
                  v{{ row.original.blueprint.version }}
                </UBadge>
              </div>
            </template>

            <template #overviewField1-cell="{ row }">
              <div v-if="getOverviewField(row.original, 0)" class="text-sm text-gray-700 dark:text-gray-300">
                <span class="font-medium text-gray-500 dark:text-gray-400">{{ getOverviewField(row.original, 0)?.label ? getOverviewField(row.original, 0)!.label + ': ' : '' }}</span>
                <span class="font-semibold text-gray-900 dark:text-white">{{ getOverviewField(row.original, 0)?.value }}</span>
              </div>
            </template>

            <template #overviewField2-cell="{ row }">
              <div v-if="getOverviewField(row.original, 1)" class="text-sm text-gray-700 dark:text-gray-300">
                <span class="font-medium text-gray-500 dark:text-gray-400">{{ getOverviewField(row.original, 1)?.label ? getOverviewField(row.original, 1)!.label + ': ' : '' }}</span>
                <span class="font-semibold text-gray-900 dark:text-white">{{ getOverviewField(row.original, 1)?.value }}</span>
              </div>
            </template>

            <template #group-cell="{ row }">
              <div class="flex items-center gap-2">
                <UIcon name="i-heroicons-users" class="w-4 h-4 text-gray-400" />
                <span class="text-sm text-gray-600 dark:text-gray-400 font-medium">{{ resolveGroupName(row.original.currentStep?.group) || '-' }}</span>
              </div>
            </template>

            <template #currentStep-cell="{ row }">
              <span class="text-sm font-medium text-gray-900 dark:text-white">{{ row.original.currentStep?.name || row.original.currentStep }}</span>
            </template>

            <template #workflowState-cell="{ row }">
              <div class="flex flex-wrap items-center gap-1.5">
                <!-- Main Status Badge -->
                <UBadge
                  v-if="row.original.workflowState"
                  :color="getStateColor(row.original.workflowState)"
                  :variant="row.original.workflowState === 'RETRYING_MICROTECH' ? 'solid' : 'subtle'"
                  size="md"
                  class="font-bold uppercase tracking-wider text-[10px] px-2 py-0.5 rounded-full"
                >
                  {{ translateWorkflowState(row.original.workflowState) }}
                </UBadge>

                <template v-if="row.original.data?.inquiry_to || isMyInquiryAnswered(row.original)">
                  <UBadge
                    v-if="row.original.data?.inquiry_to"
                    color="pink"
                    variant="subtle"
                    size="md"
                    class="font-extrabold uppercase tracking-wider text-[10px] px-2.5 py-0.5 rounded-full"
                  >
                    {{ isInquiryForMe(row.original) ? 'Rückfrage an mich' : `Rückfrage an: ${row.original.data.inquiry_to}` }}
                  </UBadge>
                  <UBadge
                    v-if="isMyInquiryAnswered(row.original)"
                    color="purple"
                    variant="subtle"
                    size="md"
                    class="font-extrabold uppercase tracking-wider text-[10px] px-2.5 py-0.5 rounded-full"
                  >
                    Rückfrage beantwortet
                  </UBadge>
                </template>
              </div>
            </template>

            <template #claimedBy-cell="{ row }">
              <div class="flex items-center gap-2">
                <UIcon v-if="row.original.claimedBy" name="i-heroicons-user" class="w-4 h-4 text-gray-400 dark:text-gray-500" />
                <span class="text-sm font-medium" :class="isClaimedByMe(row.original) ? 'text-gray-900 dark:text-white font-bold' : 'text-gray-600 dark:text-gray-400'">
                  {{ row.original.claimedBy === 'SYSTEM' ? 'System' : (row.original.claimedBy || '-') }}
                </span>
              </div>
            </template>

            <template #startTime-cell="{ row }">
              <span class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {{ formatDate(row.original.startTime) }}
              </span>
            </template>

            <template #actions-cell="{ row }">
              <div class="flex items-center justify-end gap-2">
                <template v-if="isTaskOpen(row.original)">
                  <UTooltip v-if="canManageWorkflows" text="Workflow beenden">
                    <UButton
                      color="error"
                      icon="i-heroicons-x-circle"
                      variant="ghost"
                      size="sm"
                      class="rounded-lg hover:bg-red-50 dark:hover:bg-red-950/50"
                      @click.stop="openCancelModal(row.original)"
                    />
                  </UTooltip>
                  <UTooltip text="Rückfrage zurückholen">
                    <UButton
                      v-if="canForcePull(row.original)"
                      :loading="claiming === row.original.guid"
                      color="warning"
                      icon="i-lucide-undo-2"
                      variant="ghost"
                      size="sm"
                      class="rounded-lg"
                      @click.stop="forcePullInquiry(row.original)"
                    />
                  </UTooltip>
                  <UTooltip :text="row.original.claimedBy ? (isClaimedByMe(row.original) ? 'Aufgabe freigeben' : (canForceUnclaim(row.original) ? 'In Gruppe zurückholen' : `Bearbeitet von ${row.original.claimedBy}`)) : 'Aufgabe übernehmen'">
                    <UButton
                      v-if="!row.original.claimedBy"
                      :loading="claiming === row.original.guid"
                      color="neutral"
                      icon="i-lucide-user-round-arrow-left"
                      variant="ghost"
                      size="sm"
                      class="rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                      @click.stop="claimTask(row.original)"
                    />
                    <UButton
                      v-else-if="isClaimedByMe(row.original)"
                      :loading="claiming === row.original.guid"
                      color="neutral"
                      icon="i-lucide-user-round-x"
                      variant="ghost"
                      size="sm"
                      class="rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                      @click.stop="unclaimTask(row.original)"
                    />
                    <UButton
                      v-else-if="canForceUnclaim(row.original)"
                      :loading="claiming === row.original.guid"
                      color="warning"
                      icon="i-lucide-user-round-x"
                      variant="ghost"
                      size="sm"
                      class="rounded-lg"
                      @click.stop="forceUnclaimTask(row.original)"
                    />
                    <UIcon v-else name="i-heroicons-lock-closed" class="w-4 h-4 text-gray-400 mr-2" />
                  </UTooltip>
                </template>
                
                <UButton
                  :to="embedded ? `/embedded/tasks/i/${row.original.guid}` : `/tasks/${row.original.guid}`"
                  color="neutral"
                  icon="i-heroicons-chevron-right"
                  variant="ghost"
                  size="sm"
                  class="rounded-lg group-hover:translate-x-1 transition-transform text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white"
                />
              </div>
            </template>

            <template #empty>
              <div class="flex flex-col items-center justify-center py-20 text-muted">
                <div class="mb-6 relative">
                  <div class="absolute inset-0 bg-gray-200/50 dark:bg-gray-800/50 blur-2xl rounded-full"></div>
                  <UIcon class="w-20 h-20 text-gray-400 dark:text-gray-500 relative" name="i-heroicons-sparkles" />
                </div>
                <div class="text-center space-y-3 max-w-sm px-4">
                  <p class="text-2xl font-black text-gray-900 dark:text-white tracking-tight">
                    {{ tasks.length > 0 ? 'Keine Treffer' : 'Alles erledigt!' }}
                  </p>
                  <p class="text-gray-500 dark:text-gray-400">
                    {{
                      tasks.length > 0
                        ? 'Versuchen Sie es mit anderen Suchbegriffen oder setzen Sie die Filter zurück.'
                        : 'Hervorragende Arbeit! Es sind momentan keine weiteren Aufgaben ausstehend.'
                    }}
                  </p>
                </div>
              </div>
            </template>
          </UTable>
        </UCard>
      </div>
    </UPageBody>

    <!-- Blueprint Modal (Graphical + YAML) -->
    <InstanceBlueprintModal
      v-model:open="showBlueprintModal"
      :blueprint="selectedBlueprint"
      :instance-id="selectedTaskGuid"
      :blueprint-id="selectedBlueprintId"
      :blueprint-version="selectedBlueprintVersion"
    />

    <!-- Cancel Workflow Modal (Shared Component) -->
    <CancelWorkflowModal
      v-model:open="showCancelModal"
      v-model:reason="cancelReason"
      :task="taskToCancel"
      :loading="cancelling"
      @confirm="confirmCancelTask"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { translateWorkflowState, getWorkflowStateColor } from '@/utils/translations'
import type { InstanceState, WorkflowBlueprint } from '@/types'
import { useAuthStore } from '@/stores/auth'
import { useGroupNames } from '@/composables/useGroupNames'
import { PERMISSIONS, hasPermission } from '@/utils/roles'
import InstanceBlueprintModal from '@/components/task/InstanceBlueprintModal.vue'
import CancelWorkflowModal from '@/components/task/CancelWorkflowModal.vue'

const props = defineProps<{
  embedded?: boolean
  tenant?: string
}>()

const isMounted = ref(false)
const router = useRouter()
const authStore = useAuthStore()
const { resolveGroupName } = useGroupNames()
const toast = useToast()
const loading = ref(false)
const claiming = ref<string | null>(null)
const tasks = ref<InstanceState[]>([])
const lastUpdated = ref<Date | null>(null)
const pollInterval = ref<ReturnType<typeof setInterval> | null>(null)

const canManageWorkflows = computed(() => {
  return hasPermission(authStore.userInfo?.user.permissions, PERMISSIONS.WORKFLOW_ADMIN)
})

const activeTab = ref<'my-tasks' | 'all-instances'>('my-tasks')
const tabs = [
  { label: 'Meine Aufgaben', value: 'my-tasks', icon: 'i-heroicons-user' },
  { label: 'Alle Workflow-Instanzen', value: 'all-instances', icon: 'i-heroicons-queue-list' },
]

const showCancelModal = ref(false)
const taskToCancel = ref<InstanceState | null>(null)
const cancelReason = ref('')
const cancelling = ref(false)

const openCancelModal = (task: InstanceState) => {
  taskToCancel.value = task
  cancelReason.value = ''
  showCancelModal.value = true
}

const confirmCancelTask = async () => {
  if (!taskToCancel.value) return
  cancelling.value = true
  try {
    await axios.post(`/api/workflow/v1/tasks/${taskToCancel.value.guid}/cancel`, {
      reason: cancelReason.value || undefined,
    })
    toast.add({
      title: 'Workflow beendet',
      description: `Workflow für ${taskToCancel.value.objectNumber} wurde erfolgreich abgebrochen.`,
      color: 'success',
    })
    showCancelModal.value = false
    taskToCancel.value = null
    cancelReason.value = ''
    await fetchTasks()
  } catch (error: unknown) {
    console.error('Fehler beim Beenden des Workflows:', error)
    const errorMsg = axios.isAxiosError(error) ? error.response?.data?.error : null
    toast.add({
      title: 'Fehler',
      description: errorMsg || 'Workflow konnte nicht beendet werden.',
      color: 'danger',
    })
  } finally {
    cancelling.value = false
  }
}

const formatDate = (dateString?: string) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const searchTerm = ref('')
const sorting = ref<{ id: string; desc: boolean }[]>([])
const selectedTenantFilter = ref<{ value: string; label: string } | undefined>(undefined)
const selectedUserFilter = ref<{ value: string; label: string }[] | undefined>(undefined)
const selectedStateFilter = ref<{ value: string; label: string }[] | undefined>(undefined)
const selectedBlueprintFilter = ref<{ value: string; label: string }[] | undefined>(undefined)
const selectedGroupFilter = ref<{ value: string; label: string }[] | undefined>(undefined)
const selectedStepFilter = ref<{ value: string; label: string }[] | undefined>(undefined)

// Watch for tenant prop to set initial filter
onMounted(() => {
  if (props.tenant) {
    selectedTenantFilter.value = { value: props.tenant, label: `Mandant: ${props.tenant}` }
  }
})

const showBlueprintModal = ref(false)
const selectedBlueprint = ref<WorkflowBlueprint | null>(null)
const selectedTaskGuid = ref<string | null>(null)
const selectedBlueprintId = ref<string | null>(null)
const selectedBlueprintVersion = ref<number | string | null>(null)

const isTaskOpen = (task: InstanceState) => {
  const state = task.workflowState
  return state === 'OPEN' || state === 'RETRYING_MICROTECH' || state === 'ACTIVE'
}

const isClaimedByMe = (task: InstanceState) => {
  if (!isTaskOpen(task)) return false
  if (!task.claimedBy) return false
  const user = authStore.userInfo?.user
  if (!user) return false
  const claimedBy = task.claimedBy.toLowerCase()
  return (
    (!!user.email && user.email.toLowerCase() === claimedBy) ||
    (!!user.fullName && user.fullName.toLowerCase() === claimedBy) ||
    (!!user.name && user.name.toLowerCase() === claimedBy)
  )
}

const isInquiryForMe = (task: InstanceState) => {
  const inquiryTo = task.data?.inquiry_to
  if (!inquiryTo) return false
  const user = authStore.userInfo?.user
  if (!user) return false
  const lowerInquiryTo = inquiryTo.toLowerCase()
  return (
    (!!user.email && user.email.toLowerCase() === lowerInquiryTo) ||
    (!!user.fullName && user.fullName.toLowerCase() === lowerInquiryTo) ||
    (!!user.name && user.name.toLowerCase() === lowerInquiryTo)
  )
}

const isMyInquiryAnswered = (task: InstanceState) => {
  if (task.data?.inquiry_to) return false

  const currentStepName = task.currentStep?.name || (typeof task.currentStep === 'string' ? task.currentStep : '')

  const latestInquiryStatusEntry = [...(task.history || [])]
    .reverse()
    .find((entry) => [
      'Rückfrage gestellt',
      'Rückfrage beantwortet',
      'Rückfrage zurückgeholt',
    ].includes(entry.action))

  if (!latestInquiryStatusEntry || latestInquiryStatusEntry.action !== 'Rückfrage beantwortet') {
    return false
  }

  // Badge nur im aktuellen Schritt halten; bei Schrittwechsel ausblenden.
  const answeredStepName = latestInquiryStatusEntry.stepName || ''
  if (currentStepName && answeredStepName && currentStepName !== answeredStepName) {
    return false
  }

  return true
}

const availableTenants = computed(() => {
  const tenants = new Set<string>()
  ;(tasks.value || []).forEach((t) => {
    if (t.customerId?.tenant) {
      tenants.add(t.customerId.tenant)
    }
  })
  return Array.from(tenants).sort()
})

const tenantOptions = computed(() => {
  return availableTenants.value.map((t) => ({ value: t, label: `Mandant: ${t}` }))
})

const availableUsers = computed(() => {
  const users = new Set<string>()
  ;(tasks.value || []).forEach((t) => {
    if (t.claimedBy) {
      users.add(t.claimedBy)
    }
  })
  return Array.from(users).sort()
})

const userOptions = computed(() => {
  return availableUsers.value.map((u) => ({ value: u, label: u === 'SYSTEM' ? 'System' : u }))
})

const availableStates = computed(() => {
  const states = new Set<string>()
  let hasInquiryAnswered = false
  let hasInquiryToMe = false
  let hasAnyInquiry = false

  ;(tasks.value || []).forEach((t) => {
    if (t.workflowState) {
      states.add(t.workflowState)
    }
    if (isMyInquiryAnswered(t)) {
      hasInquiryAnswered = true
    }
    if (t.data?.inquiry_to) {
      hasAnyInquiry = true
      if (isInquiryForMe(t)) {
        hasInquiryToMe = true
      }
    }
  })

  const result = Array.from(states).sort()
  if (hasInquiryToMe) result.push('INQUIRY_TO_ME')
  if (hasAnyInquiry) result.push('INQUIRY_OPEN')
  if (hasInquiryAnswered) result.push('INQUIRY_ANSWERED')

  return result
})

const stateOptions = computed(() => {
  return availableStates.value.map((s) => ({ value: s, label: translateWorkflowState(s) }))
})

const availableBlueprints = computed(() => {
  const blueprints = new Set<string>()
  ;(tasks.value || []).forEach((t) => {
    const blueprintId = t.blueprintId || t.blueprint?.id
    if (blueprintId) {
      blueprints.add(blueprintId)
    }
  })
  return Array.from(blueprints).sort()
})

const blueprintOptions = computed(() => {
  return availableBlueprints.value.map((b) => ({ value: b, label: b }))
})

const availableGroups = computed(() => {
  const groups = new Set<string>()
  ;(tasks.value || []).forEach((t) => {
    if (t.currentStep?.group) {
      groups.add(t.currentStep.group)
    }
  })
  return Array.from(groups).sort()
})

const groupOptions = computed(() => {
  return availableGroups.value.map((g) => ({ value: g, label: resolveGroupName(g) || g }))
})

const availableSteps = computed(() => {
  const steps = new Set<string>()
  ;(tasks.value || []).forEach((t) => {
    const stepName = t.currentStep?.name || (typeof t.currentStep === 'string' ? t.currentStep : '')
    if (stepName) {
      steps.add(stepName)
    }
  })
  return Array.from(steps).sort()
})

const stepOptions = computed(() => {
  return availableSteps.value.map((s) => ({ value: s, label: s }))
})

const getStateColor = (state: string) => {
  return getWorkflowStateColor(state)
}

const filteredTasks = computed(() => {
  let result = [...(tasks.value || [])]

  if (selectedTenantFilter.value) {
    result = result.filter((t) => t.customerId?.tenant === selectedTenantFilter.value?.value)
  }

  if (selectedUserFilter.value && selectedUserFilter.value.length > 0) {
    const activeUsers = selectedUserFilter.value.map(u => u.value)
    result = result.filter((t) => t.claimedBy && activeUsers.includes(t.claimedBy))
  }

  if (selectedStateFilter.value && selectedStateFilter.value.length > 0) {
    const activeStates = selectedStateFilter.value.map(s => s.value)
    result = result.filter((t) => {
      if (activeStates.includes(t.workflowState)) return true
      if (activeStates.includes('INQUIRY_ANSWERED') && isMyInquiryAnswered(t)) return true
      if (activeStates.includes('INQUIRY_TO_ME') && isInquiryForMe(t)) return true
      if (activeStates.includes('INQUIRY_OPEN') && t.data?.inquiry_to) return true
      
      return false
    })
  }
  
  if (selectedBlueprintFilter.value && selectedBlueprintFilter.value.length > 0) {
    const activeBlueprints = selectedBlueprintFilter.value.map(b => b.value)
    result = result.filter((t) => {
      const blueprintId = t.blueprintId || t.blueprint?.id || ''
      return activeBlueprints.includes(blueprintId)
    })
  }

  if (selectedGroupFilter.value && selectedGroupFilter.value.length > 0) {
    const activeGroups = selectedGroupFilter.value.map(g => g.value)
    result = result.filter((t) => {
      const group = t.currentStep?.group || ''
      return activeGroups.includes(group)
    })
  }

  if (selectedStepFilter.value && selectedStepFilter.value.length > 0) {
    const activeSteps = selectedStepFilter.value.map(s => s.value)
    result = result.filter((t) => {
      const stepName = t.currentStep?.name || (typeof t.currentStep === 'string' ? t.currentStep : '')
      return activeSteps.includes(stepName)
    })
  }

  if (searchTerm.value) {
    const s = searchTerm.value.toLowerCase()
    result = result.filter((t) => {
      const blueprintId = t.blueprintId || t.blueprint?.id || ''
      const stepName = t.currentStep?.name || (typeof t.currentStep === 'string' ? t.currentStep : '')
      const rawGroup = t.currentStep?.group || ''
      const stepGroup = resolveGroupName(rawGroup) || rawGroup

      const matchesBasic = (
        t.objectNumber?.toLowerCase().includes(s) ||
        blueprintId.toLowerCase().includes(s) ||
        stepName.toLowerCase().includes(s) ||
        rawGroup.toLowerCase().includes(s) ||
        stepGroup.toLowerCase().includes(s) ||
        translateWorkflowState(t.workflowState || '').toLowerCase().includes(s) ||
        t.claimedBy?.toLowerCase().includes(s) ||
        (t.startTime && formatDate(t.startTime).toLowerCase().includes(s))
      )

      if (matchesBasic) return true

      // Virtual states search
      if (isMyInquiryAnswered(t) && "rückfrage beantwortet".includes(s)) return true
      if (isInquiryForMe(t) && "rückfrage an mich".includes(s)) return true
      if (t.data?.inquiry_to && "rückfrage offen".includes(s)) return true

      return false
    })
  }

  // Sorting logic
  if (sorting.value.length > 0) {
    const { id, desc } = sorting.value[0]
    result.sort((a, b) => {
      let aValue: unknown = a
      let bValue: unknown = b

      // Special handling for accessors
      if (id === 'blueprint') {
        aValue = a.blueprint?.id || a.blueprintId || ''
        bValue = b.blueprint?.id || b.blueprintId || ''
      } else if (id === 'group') {
        const rawA = a.currentStep?.group || ''
        const rawB = b.currentStep?.group || ''
        aValue = resolveGroupName(rawA) || rawA
        bValue = resolveGroupName(rawB) || rawB
      } else if (id === 'currentStep') {
        aValue = a.currentStep?.name || (typeof a.currentStep === 'string' ? a.currentStep : '')
        bValue = b.currentStep?.name || (typeof b.currentStep === 'string' ? b.currentStep : '')
      } else if (id === 'overviewField1') {
        const f1 = getOverviewField(a, 0)
        const f2 = getOverviewField(b, 0)
        aValue = f1 ? `${f1.label}: ${f1.value}` : ''
        bValue = f2 ? `${f2.label}: ${f2.value}` : ''
      } else if (id === 'overviewField2') {
        const f1 = getOverviewField(a, 1)
        const f2 = getOverviewField(b, 1)
        aValue = f1 ? `${f1.label}: ${f1.value}` : ''
        bValue = f2 ? `${f2.label}: ${f2.value}` : ''
      } else if (id === 'startTime') {
        aValue = a.startTime ? new Date(a.startTime).getTime() : 0
        bValue = b.startTime ? new Date(b.startTime).getTime() : 0
      } else {
        const path = id.split('.')
        for (const key of path) {
          aValue = (aValue as Record<string, unknown>)?.[key]
          bValue = (bValue as Record<string, unknown>)?.[key]
        }
      }

      if (aValue === bValue) return 0
      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      const comparison = typeof aValue === 'string'
        ? aValue.localeCompare(bValue as string)
        : ((aValue as number) < (bValue as number) ? -1 : 1)

      return desc ? -comparison : comparison
    })
  }

  return result
})

const getOverviewField = (task: InstanceState, index: number) => {
  const item = index === 0 ? task.overviewField1 : task.overviewField2
  if (item && item.value) {
    return { label: item.label, value: item.value }
  }
  const bpField = index === 0 ? task.blueprint?.overviewField1 : task.blueprint?.overviewField2
  if (bpField && task.data && task.data[bpField.name] !== undefined && task.data[bpField.name] !== null) {
    const val = String(task.data[bpField.name])
    if (val) {
      return { label: bpField.label, value: val }
    }
  }
  return null
}

const viewBlueprint = async (task: InstanceState) => {
  selectedBlueprint.value = task.blueprint || null
  selectedTaskGuid.value = task.guid || null
  selectedBlueprintId.value = task.blueprint?.id || task.blueprintId || null
  selectedBlueprintVersion.value = task.blueprint?.version || null
  showBlueprintModal.value = true
  if (!selectedBlueprint.value && task.guid) {
    try {
      const response = await axios.get(`/api/workflow/v1/tasks/${task.guid}/blueprint`)
      selectedBlueprint.value = response.data
    } catch (error) {
      console.error('Failed to load blueprint:', error)
    }
  }
}

const columns = [
  { accessorKey: 'objectNumber', header: 'Objektnummer', id: 'objectNumber', enableSorting: true },
  { accessorKey: 'blueprint.id', header: 'Workflow', id: 'blueprint', enableSorting: true },
  { id: 'overviewField1', header: 'Attribut1', enableSorting: true },
  { id: 'overviewField2', header: 'Attribut2', enableSorting: true },
  { accessorKey: 'currentStep.group', header: 'Gruppe', id: 'group', enableSorting: true },
  { accessorKey: 'currentStep.name', header: 'Aktueller Schritt', id: 'currentStep', enableSorting: true },
  { accessorKey: 'claimedBy', header: 'Besitzer', id: 'claimedBy', enableSorting: true },
  { accessorKey: 'workflowState', header: 'Status', id: 'workflowState', enableSorting: true },
  { accessorKey: 'startTime', header: 'Gestartet am', id: 'startTime', enableSorting: true },
  { id: 'actions', header: '' },
]

const fetchTasks = async () => {
  if (isMounted.value) {
    loading.value = true
  }
  try {
    const endpoint = (canManageWorkflows.value && activeTab.value === 'all-instances')
      ? '/api/workflow/v1/instances'
      : '/api/workflow/v1/tasks'
    const response = await axios.get(endpoint, {
      params: { slim_history: 'true' },
    })
    if (!isMounted.value) return
    tasks.value = response.data || []
    lastUpdated.value = new Date()
  } catch (error) {
    if (!isMounted.value) return
    console.error('Fehler beim Laden der Aufgaben:', error)
  } finally {
    if (isMounted.value) {
      loading.value = false
    }
  }
}

watch(activeTab, () => {
  fetchTasks()
})

const claimTask = async (task: InstanceState) => {
  claiming.value = task.guid
  try {
    await axios.post(`/api/workflow/v1/tasks/${task.guid}/claim`)
    if (!isMounted.value) return
    router.push(props.embedded ? `/embedded/tasks/i/${task.guid}` : `/tasks/${task.guid}`)
  } catch (error) {
    if (!isMounted.value) return
    console.error('Fehler beim Übernehmen der Aufgabe:', error)
  } finally {
    if (isMounted.value) {
      claiming.value = null
    }
  }
}

const unclaimTask = async (task: InstanceState) => {
  claiming.value = task.guid
  try {
    await axios.post(`/api/workflow/v1/tasks/${task.guid}/unclaim`)
    if (!isMounted.value) return
    await fetchTasks()
  } catch (error) {
    if (!isMounted.value) return
    console.error('Fehler beim Freigeben der Aufgabe:', error)
  } finally {
    if (isMounted.value) {
      claiming.value = null
    }
  }
}

const forceUnclaimTask = async (task: InstanceState) => {
  if (!confirm('Möchten Sie die Aufgabe wirklich in die Gruppe zurückholen? Der aktuelle Bearbeiter wird darüber informiert.')) return

  claiming.value = task.guid
  try {
    await axios.post(`/api/workflow/v1/tasks/${task.guid}/force-unclaim`)
    if (!isMounted.value) return
    await fetchTasks()
  } catch (error) {
    if (!isMounted.value) return
    console.error('Fehler beim Zurückholen der Aufgabe:', error)
  } finally {
    if (isMounted.value) {
      claiming.value = null
    }
  }
}

const canForcePull = (task: InstanceState) => {
  if (!isTaskOpen(task)) return false
  const data = task.data
  if (!data?.inquiry_to) return false

  const user = authStore.userInfo?.user
  if (!user) return false

  // Current owner of the task
  if (isClaimedByMe(task)) {
    return true
  }

  // Initiator
  if (data.inquiry_from_user === user.email || data.inquiry_from_user === user.fullName || data.inquiry_from_user === user.name) {
    return true
  }

  // Group member
  if (data.inquiry_from_group) {
    return !!(user.groups?.includes(data.inquiry_from_group) || user.groupMemberships?.includes(data.inquiry_from_group))
  }

  return false
}

const canForceUnclaim = (task: InstanceState) => {
  if (!isTaskOpen(task)) return false
  if (!task.claimedBy || isClaimedByMe(task)) return false
  const group = task.currentStep?.group
  if (!group) return false
  const user = authStore.userInfo?.user
  return !!(user?.groups?.includes(group) || user?.groupMemberships?.includes(group))
}

const forcePullInquiry = async (task: InstanceState) => {
  if (!confirm('Möchten Sie die Rückfrage wirklich zurückholen? Der Kollege wird darüber informiert.')) return

  claiming.value = task.guid
  try {
    await axios.post(`/api/workflow/v1/tasks/${task.guid}/complete`, {
      ...task.data,
      action: 'FORCE_PULL_INQUIRY'
    })
    if (!isMounted.value) return
    router.push(props.embedded ? `/embedded/tasks/i/${task.guid}` : `/tasks/${task.guid}`)
  } catch (error) {
    if (!isMounted.value) return
    console.error('Fehler beim Zurückholen der Rückfrage:', error)
  } finally {
    if (isMounted.value) {
      claiming.value = null
    }
  }
}

onMounted(() => {
  isMounted.value = true
  fetchTasks()
  pollInterval.value = setInterval(fetchTasks, 30000) // Poll every 30 seconds
})

onBeforeUnmount(() => {
  isMounted.value = false
  if (pollInterval.value) clearInterval(pollInterval.value)
})
</script>
